import {
    Box, Chip, Divider, Grid, Typography,
} from '@mui/material';
import { Check } from '@mui/icons-material';
import {
    useRef, useState,
} from 'react';
import type { CSSProperties } from 'react';
import {
    type BillingPeriod,
    SUBSCRIPTION_ANNUAL_FEATURE,
    SUBSCRIPTION_MONTHLY_FEATURE,
    SUBSCRIPTION_PLAN_FEATURES,
    SUBSCRIPTION_PLANS,
    type SubscriptionPlan,
    getAnnualSavings,
    getAnnualTotal,
    getEffectiveMonthly,
} from '../../../../common/subscriptionPlans';
import Button from '../../foundation/Button';
import { CenteredFlex } from '../../foundation/Flex';
import {
    BillingToggleContainer,
    ConfettiPieceStyled,
    CustomPrice,
    DiscountBadge,
    DiscountBurst,
    GiftButton,
    GiftCloseButton,
    GiftCoupon,
    GiftCouponBig,
    GiftCouponButton,
    GiftCouponSub,
    GiftCouponTop,
    GiftHint,
    GiftModal,
    GiftOverlay,
    PopularBadge,
    SavingsChip,
    SubscriptionCard,
    ToggleOption,
    WhatsAppButton,
} from './SubscriptionPlans.styles';

export type SubscriptionPlansProps = {
    onPlanClick: (plan: SubscriptionPlan, billingPeriod: BillingPeriod) => void
    showHeader?: boolean
    showBillingToggle?: boolean
    showEnterpriseCta?: boolean
    defaultBillingPeriod?: BillingPeriod
}

type ConfettiPiece = {
    id: number
    left: string
    dx: string
    color: string
}

const confettiColors = ['#00843f', '#ed8936', '#10334b', '#f6c945', '#4fb477'];

const makeConfetti = (): ConfettiPiece[] => Array.from({ length: 28 }, (_, i) => ({
    id: Date.now() + i,
    left: `${42 + Math.random() * 16}%`,
    dx: `${Math.random() * 240 - 120}px`,
    color: confettiColors[i % confettiColors.length],
}));

const getBillingFeature = (billingPeriod: BillingPeriod) => (
    billingPeriod === 'annual' ? SUBSCRIPTION_ANNUAL_FEATURE : SUBSCRIPTION_MONTHLY_FEATURE
);

const getPlanDisplayPrice = (plan: SubscriptionPlan, billingPeriod: BillingPeriod, promoUnlocked: boolean) => {
    const monthlyPrice = promoUnlocked && plan.promoMonthlyPrice ? plan.promoMonthlyPrice : plan.monthlyPrice;
    const annualBasePrice = plan.promoMonthlyPrice ?? plan.monthlyPrice;

    return billingPeriod === 'annual' ? getEffectiveMonthly(annualBasePrice) : monthlyPrice;
};

const getAnnualBasePrice = (plan: SubscriptionPlan) => plan.promoMonthlyPrice ?? plan.monthlyPrice;

const SubscriptionPlans = ({
    onPlanClick,
    showHeader = true,
    showBillingToggle = true,
    defaultBillingPeriod = 'monthly',
}: SubscriptionPlansProps): JSX.Element => {
    const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(defaultBillingPeriod);
    const [giftVisible, setGiftVisible] = useState<boolean>(true);
    const [giftOpening, setGiftOpening] = useState<boolean>(false);
    const [couponVisible, setCouponVisible] = useState<boolean>(false);
    const [promoUnlocked, setPromoUnlocked] = useState<boolean>(false);
    const [flashPromoCard, setFlashPromoCard] = useState<boolean>(false);
    const [confettiPieces, setConfettiPieces] = useState<ConfettiPiece[]>([]);
    const promoCardRef = useRef<HTMLDivElement | null>(null);

    const openGift = () => {
        if (giftOpening) return;

        setGiftOpening(true);
        setConfettiPieces(makeConfetti());

        window.setTimeout(() => {
            setCouponVisible(true);
        }, 480);

        window.setTimeout(() => {
            setConfettiPieces([]);
        }, 1400);
    };

    const closeGift = () => {
        setPromoUnlocked(true);
        setGiftVisible(false);
    };

    const redeemGift = () => {
        closeGift();
        setFlashPromoCard(true);
        promoCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

        window.setTimeout(() => {
            setFlashPromoCard(false);
        }, 1700);
    };

    return (
        <Box>
            {giftVisible && (
                <GiftOverlay>
                    <GiftModal>
                        <GiftCloseButton type="button" onClick={closeGift} aria-label="סגירה">×</GiftCloseButton>
                        {!couponVisible && (
                            <GiftButton type="button" opening={giftOpening} onClick={openGift}>
                                <div className="gift-emoji">🎁</div>
                                <GiftHint>הפתעת קיץ בפנים — לחצו לפתיחה</GiftHint>
                            </GiftButton>
                        )}
                        {couponVisible && (
                            <GiftCoupon>
                                <GiftCouponTop>☀️ מבצע קיץ</GiftCouponTop>
                                <GiftCouponBig>38%- הנחה</GiftCouponBig>
                                <GiftCouponSub>משרה אחת ב-180 ₪ במקום 290</GiftCouponSub>
                                <GiftCouponButton type="button" onClick={redeemGift}>למימוש המבצע</GiftCouponButton>
                            </GiftCoupon>
                        )}
                        {confettiPieces.map(piece => (
                            <ConfettiPieceStyled
                                key={piece.id}
                                style={{
                                    left: piece.left,
                                    background: piece.color,
                                    '--dx': piece.dx,
                                } as CSSProperties}
                            />
                        ))}
                    </GiftModal>
                </GiftOverlay>
            )}

            {showHeader && (
                <Box textAlign="center" marginBottom="2.5rem">
                    <Typography color="primary" fontWeight={800} variant="h3" marginBottom="0.75rem">
                        מנוי משרות מקודמות
                    </Typography>
                    <Typography color="secondary" variant="h6" maxWidth="37.5rem" margin="0 auto" lineHeight={1.7}>
                        הגיעו למועמדים הכי רלוונטיים עם קידום בעדיפות גבוהה,
                        סוכן AI ב-WhatsApp ודיוור ישיר
                    </Typography>
                </Box>
            )}

            {showBillingToggle && (
                <CenteredFlex marginBottom="2.5rem">
                    <BillingToggleContainer>
                        <ToggleOption
                            isActive={billingPeriod === 'annual'}
                            onClick={() => setBillingPeriod('annual')}
                        >
                            שנתי
                            <Chip
                                size="small"
                                label="25% הנחה"
                                style={{
                                    background: billingPeriod === 'annual'
                                        ? 'rgba(255,255,255,0.25)'
                                        : 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
                                    color: billingPeriod === 'annual' ? '#fff' : '#2e7d32',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                }}
                            />
                        </ToggleOption>
                        <ToggleOption
                            isActive={billingPeriod === 'monthly'}
                            onClick={() => setBillingPeriod('monthly')}
                        >
                            חודשי
                        </ToggleOption>
                    </BillingToggleContainer>
                </CenteredFlex>
            )}

            <Grid container spacing={3} alignItems="stretch" justifyContent="center">
                {SUBSCRIPTION_PLANS[billingPeriod].map((plan: SubscriptionPlan, index) => {
                    const isDiscounted = promoUnlocked && Boolean(plan.promoMonthlyPrice);
                    const annualBasePrice = getAnnualBasePrice(plan);
                    const displayPrice = getPlanDisplayPrice(plan, billingPeriod, promoUnlocked);
                    const annualTotal = getAnnualTotal(annualBasePrice);
                    const savings = getAnnualSavings(annualBasePrice);

                    return (
                        <Grid item xs={12} sm={6} md={4} key={plan.id}>
                            <SubscriptionCard
                                ref={index === 0 ? promoCardRef : undefined}
                                isPopular={plan.popular && !isDiscounted}
                                isDiscounted={isDiscounted}
                                isFlashing={flashPromoCard && index === 0}
                            >
                                {isDiscounted && (
                                    <>
                                        <DiscountBadge>☀️ מבצע קיץ</DiscountBadge>
                                        <DiscountBurst><b>38%-</b><small>הנחה</small></DiscountBurst>
                                    </>
                                )}
                                {plan.popular && (
                                    <PopularBadge>★ הכי משתלם</PopularBadge>
                                )}

                                <Box textAlign="center" marginBottom="1.5rem" marginTop={plan.popular ? 1 : 0}>
                                    <Typography color="secondary" variant="h5" fontWeight={700} marginBottom="0.25rem">
                                        {plan.name}
                                    </Typography>
                                    {plan.subtitle && (
                                        <Typography color="secondary" fontWeight={500}>
                                            {plan.subtitle}
                                        </Typography>
                                    )}
                                </Box>

                                {plan.contactOnly ? (
                                    <>
                                        <CustomPrice>{plan.customPriceText}</CustomPrice>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="large"
                                            fullWidth
                                            onClick={() => { window.location.assign('/home/contactus'); }}
                                            style={{ marginBottom: '1.5rem', padding: '0.75rem', fontWeight: 700 }}
                                        >
                                            דברו איתנו
                                        </Button>
                                    </>
                                ) : (
                                    <Box textAlign="center" marginBottom="1.5rem">
                                        <Box display="flex" alignItems="baseline" justifyContent="center" gap="0.25rem">
                                            {(billingPeriod === 'annual' || isDiscounted) && (
                                                <Typography
                                                    component="span"
                                                    color="secondary"
                                                    fontWeight={500}
                                                    style={{ textDecoration: 'line-through', fontSize: '1.3rem', opacity: 0.5 }}
                                                >
                                                    {billingPeriod === 'annual' ? annualBasePrice : plan.monthlyPrice}
                                                </Typography>
                                            )}
                                            <Typography
                                                component="span"
                                                color={isDiscounted || plan.popular ? 'primary' : 'secondary'}
                                                fontWeight={800}
                                                style={{ fontSize: '3rem', lineHeight: 1 }}
                                            >
                                                {displayPrice}
                                            </Typography>
                                            <Typography component="span" color="secondary" fontWeight={600} style={{ fontSize: '1.2rem' }}>
                                                ₪
                                            </Typography>
                                        </Box>
                                        <Typography color="secondary" variant="body2" marginTop="0.25rem">
                                            לחודש + מע&quot;מ
                                        </Typography>

                                        {billingPeriod === 'annual' && (
                                            <Box marginTop="0.75rem" display="flex" flexDirection="column" alignItems="center" gap="0.25rem">
                                                <Typography color="secondary" variant="body2">
                                                    {annualTotal.toLocaleString()} ₪ לשנה
                                                </Typography>
                                                <SavingsChip
                                                    label={`25% הנחה - חוסכים ${savings} ₪ בשנה`}
                                                    size="small"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {!plan.contactOnly && (
                                    <Button
                                        variant={isDiscounted || plan.popular ? 'contained' : 'outlined'}
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        onClick={() => onPlanClick(plan, billingPeriod)}
                                        style={{ marginBottom: '1.5rem', padding: '0.75rem', fontWeight: 700 }}
                                    >
                                        בחרו מסלול
                                    </Button>
                                )}

                                <Divider style={{ marginBottom: '1rem' }} />

                                <Box flex={1}>
                                    <Typography color="secondary" fontWeight={700} variant="subtitle2" marginBottom="0.75rem">
                                        {plan.featureTitle ?? 'כל מסלול כולל:'}
                                    </Typography>
                                    {(plan.customFeatures ?? [...SUBSCRIPTION_PLAN_FEATURES, getBillingFeature(billingPeriod)]).map(feature => (
                                        <Box key={feature} display="flex" marginBottom="0.75rem" alignItems="flex-start" gap={1}>
                                            <Check color="primary" style={{ fontSize: 22, marginTop: 2, flexShrink: 0 }} />
                                            <Typography color="secondary" variant="body2">{feature}</Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {plan.contactOnly && (
                                    <WhatsAppButton href="https://wa.me/972544700720" target="_blank" rel="noopener">
                                        דברו איתנו בוואטסאפ
                                    </WhatsAppButton>
                                )}
                            </SubscriptionCard>
                        </Grid>
                    );
                })}
            </Grid>

        </Box>
    );
};

export default SubscriptionPlans;
